<template>
  <div v-if="isResuming" class="auth-register__resume" role="status" aria-live="polite">
    <p class="auth-login__hint">Loading your registration...</p>
  </div>

  <Transition v-else name="step-fade" mode="out-in">
    <ClassicChooseRoleStep
      v-if="currentStep === STEPS.CHOOSE_ROLE && isClassicFlow"
      :login-url="config.loginUrl"
      :practice-url="config.practiceUrl"
      :patient-url="config.patientUrl"
    />

    <StepChooseRole
      v-else-if="currentStep === STEPS.CHOOSE_ROLE"
      :support-phone="config.supportPhone"
      :login-url="config.loginUrl"
      :practice-url="config.practiceUrl"
      :patient-url="config.patientUrl"
    />

    <StepPatientForm
      v-else-if="currentStep === STEPS.PATIENT_FORM"
      :loading="isLoading"
      :form-error="formError"
      :field-errors="fieldErrors"
      :initial-code="config.initialCode"
      :initial-company-name="config.initialCompanyName"
      :initial-referral-error="config.initialReferralError"
      :salutations="config.salutations"
      :genders="config.genders"
      :terms-link="config.termsLink"
      :privacy-link="config.privacyLink"
      :consent-link="config.consentLink"
      :login-url="config.loginUrl"
      :support-phone="config.supportPhone"
      :show-video="config.showVideo"
      :video-url="config.videoUrl"
      :video-title="config.videoTitle"
      :video-poster="config.videoPoster"
      :classic="isClassicFlow"
      @submit="onPatientSubmit"
    />

    <StepPracticeVerify
      v-else-if="currentStep === STEPS.PATIENT_VERIFY"
      flow="patient"
      :needs-method-selection="flowData.needsMethodSelection"
      :verification-method="flowData.verificationMethod"
      :email="flowData.verifyEmail"
      :phone="flowData.verifyPhone"
      :loading="isLoading"
      :error="formError"
      :resend-feedback="resendFeedback"
      :is-cooling-down="resendCooldown.isCoolingDown"
      :countdown="resendCooldown.formattedCountdown"
      :support-phone="config.supportPhone"
      :classic="isClassicFlow"
      @select-method="onSelectVerifyMethod"
      @resend-email="onResendEmail"
      @resend-phone="onResendPhone"
      @verify-phone="onVerifyPhone"
    />

    <StepPracticeForm
      v-else-if="currentStep === STEPS.PRACTICE_FORM"
      :loading="isLoading"
      :form-error="formError"
      :field-errors="fieldErrors"
      :login-url="config.loginUrl"
      :support-phone="config.supportPhone"
      :maps-api-key="config.mapsApiKey"
      :google-places-enabled="config.googlePlacesEnabled"
      :npi-search-link="config.npiSearchLink"
      :classic="isClassicFlow"
      @submit="onPracticeSubmit"
    />

    <StepPracticeVerify
      v-else-if="currentStep === STEPS.PRACTICE_VERIFY"
      flow="practice"
      :needs-method-selection="flowData.needsMethodSelection"
      :verification-method="flowData.verificationMethod"
      :email="flowData.verifyEmail"
      :phone="flowData.verifyPhone"
      :loading="isLoading"
      :error="formError"
      :resend-feedback="resendFeedback"
      :is-cooling-down="resendCooldown.isCoolingDown"
      :countdown="resendCooldown.formattedCountdown"
      :support-phone="config.supportPhone"
      :classic="isClassicFlow"
      @select-method="onSelectVerifyMethod"
      @resend-email="onResendEmail"
      @resend-phone="onResendPhone"
      @verify-phone="onVerifyPhone"
    />

    <StepAccountManager
      v-else-if="currentStep === STEPS.ACCOUNT_MANAGER"
      :loading="isLoading"
      :form-error="formError"
      :field-errors="fieldErrors"
      :support-phone="config.supportPhone"
      :classic="isClassicFlow"
      @submit="onManagerSubmit"
      @skip="onManagerSkip"
    />

    <StepApprovalWait
      v-else-if="currentStep === STEPS.APPROVAL_WAIT"
      :support-phone="config.supportPhone"
      :classic="isClassicFlow"
    />
  </Transition>
</template>

<script setup>
import { computed, onMounted, provide, ref, watch } from 'vue'
import { FLOWS, STEPS, VERIFY_METHOD } from './constants/steps.js'
import { useRegisterFlow } from './composables/useRegisterFlow.js'
import { useEmailVerificationPoll } from './composables/useEmailVerificationPoll.js'
import { useResendCooldown } from '../shared/composables/useResendCooldown.js'
import { useAuthAccessibility } from '../shared/composables/useAuthAccessibility.js'
import {
  extractFieldErrors,
  extractRegisterError,
  registerApi,
} from './services/registerApi.js'
import StepChooseRole from './steps/StepChooseRole.vue'
import StepPatientForm from './steps/StepPatientForm.vue'
import StepPracticeForm from './steps/StepPracticeForm.vue'
import StepPracticeVerify from './steps/StepPracticeVerify.vue'
import StepAccountManager from './steps/StepAccountManager.vue'
import StepApprovalWait from './steps/StepApprovalWait.vue'
import ClassicChooseRoleStep from '../classic/register/ClassicChooseRoleStep.vue'

const props = defineProps({
  config: { type: Object, required: true },
})

const isClassicFlow = computed(() => props.config.flowMode === 'classic')

const { currentStep, flowData, navigateTo, setFlowData } = useRegisterFlow(
  initialStepForFlow(props.config.flow),
)

const isLoading = ref(false)
const isResuming = ref(
  props.config.canResume
  && (props.config.flow === FLOWS.PATIENT || props.config.flow === FLOWS.PRACTICE),
)
const formError = ref('')
const fieldErrors = ref({})
const resendFeedback = ref('')
const resendCooldown = useResendCooldown(60)

const STEP_ANNOUNCEMENTS = {
  [STEPS.CHOOSE_ROLE]: 'Registration. Select your registration type.',
  [STEPS.PATIENT_FORM]: 'Patient registration form.',
  [STEPS.PATIENT_VERIFY]: 'Verify your patient account.',
  [STEPS.PRACTICE_FORM]: 'Practice registration form.',
  [STEPS.PRACTICE_VERIFY]: 'Verify your practice account.',
  [STEPS.ACCOUNT_MANAGER]: 'Create an optional account manager.',
  [STEPS.APPROVAL_WAIT]: 'Application submitted for approval.',
}

const { announcement } = useAuthAccessibility(currentStep, formError, STEP_ANNOUNCEMENTS)

provide('authLoginAnnouncement', announcement)
provide('authLegalLinks', computed(() => ({
  termsLink: props.config.termsLink,
  privacyLink: props.config.privacyLink,
  consentLink: props.config.consentLink,
})))
provide('authBrandLogoUrl', props.config.brandLogoUrl)

const emailPoll = useEmailVerificationPoll(
  () => (currentStep.value === STEPS.PATIENT_VERIFY
    ? registerApi.getPatientVerificationStatus()
    : registerApi.getVerificationStatus()),
  () => {
    if (currentStep.value === STEPS.PATIENT_VERIFY) {
      completePatientVerification()
      return
    }

    navigateTo(STEPS.ACCOUNT_MANAGER)
    clearErrors()
  },
)

async function completePatientVerification() {
  try {
    const { data } = await registerApi.getPatientStatus()
    handleRedirect(data)
  } catch {
    formError.value = 'Verification succeeded, but we could not continue. Please refresh the page.'
  } finally {
    clearErrors()
  }
}

function initialStepForFlow(flow) {
  if (flow === FLOWS.PATIENT) return STEPS.PATIENT_FORM
  if (flow === FLOWS.PRACTICE) return STEPS.PRACTICE_FORM
  return STEPS.CHOOSE_ROLE
}

function clearErrors() {
  formError.value = ''
  fieldErrors.value = {}
}

function handleRedirect(data) {
  if (data?.redirect) {
    window.location.href = data.redirect
    return true
  }
  return false
}

function applyPatientStatus(data) {
  if (handleRedirect(data)) return

  if (data?.message && !data?.step) {
    formError.value = data.message
    return
  }

  setFlowData({
    verificationMethod: data.verification_method ?? null,
    needsMethodSelection: Boolean(data.needs_method_selection),
    verifyEmail: data.email ?? '',
    verifyPhone: data.phone_number ?? '',
  })

  if (data.step === 2) {
    navigateTo(STEPS.PATIENT_VERIFY)
    if (data.verification_method === VERIFY_METHOD.EMAIL) {
      emailPoll.start()
    }
  } else {
    navigateTo(STEPS.PATIENT_FORM)
  }
}

function applyPracticeStatus(data) {
  if (handleRedirect(data)) return

  if (data?.message && !data?.step) {
    formError.value = data.message
    return
  }

  setFlowData({
    practiceStep: data.step,
    verificationMethod: data.verification_method ?? null,
    needsMethodSelection: Boolean(data.needs_method_selection),
    verifyEmail: data.email ?? '',
    verifyPhone: data.phone_number ?? '',
  })

  if (data.step === 2) {
    navigateTo(STEPS.PRACTICE_VERIFY)
    if (data.verification_method === VERIFY_METHOD.EMAIL) {
      emailPoll.start()
    }
  } else if (data.step === 3) {
    navigateTo(STEPS.ACCOUNT_MANAGER)
  } else if (data.step === 4) {
    navigateTo(STEPS.APPROVAL_WAIT)
  } else {
    navigateTo(STEPS.PRACTICE_FORM)
  }
}

async function loadPatientStatus() {
  const { data } = await registerApi.getPatientStatus()
  applyPatientStatus(data)
}

async function loadPracticeStatus() {
  const { data } = await registerApi.getPracticeStatus()
  applyPracticeStatus(data)
}

async function onPatientSubmit(form) {
  clearErrors()
  isLoading.value = true

  try {
    const { data } = await registerApi.registerPatient(form)
    applyPatientStatus(data)
  } catch (error) {
    fieldErrors.value = extractFieldErrors(error)
    formError.value = extractRegisterError(error, 'email')
  } finally {
    isLoading.value = false
  }
}

async function onPracticeSubmit(form) {
  clearErrors()
  isLoading.value = true

  try {
    const { data } = await registerApi.registerPractice(form)
    applyPracticeStatus(data)
  } catch (error) {
    fieldErrors.value = extractFieldErrors(error)
    formError.value = extractRegisterError(error, 'email')
  } finally {
    isLoading.value = false
  }
}

async function onSelectVerifyMethod(method) {
  clearErrors()
  isLoading.value = true

  const updateMethod = currentStep.value === STEPS.PATIENT_VERIFY
    ? registerApi.updatePatientVerifyMethod
    : registerApi.updateVerifyMethod

  try {
    const { data } = await updateMethod(method)
    setFlowData({
      verificationMethod: data.verification_method,
      needsMethodSelection: false,
    })

    if (method === VERIFY_METHOD.EMAIL) {
      emailPoll.start()
    }
  } catch (error) {
    formError.value = extractRegisterError(error, 'type')
  } finally {
    isLoading.value = false
  }
}

async function onResendEmail() {
  if (resendCooldown.isCoolingDown) return

  clearErrors()
  isLoading.value = true

  const resend = currentStep.value === STEPS.PATIENT_VERIFY
    ? registerApi.resendPatientVerificationEmail
    : registerApi.resendVerificationEmail

  try {
    await resend()
    resendFeedback.value = 'A new verification link has been sent to your email.'
    resendCooldown.startCooldown()
  } catch (error) {
    formError.value = extractRegisterError(error, 'email')
  } finally {
    isLoading.value = false
  }
}

async function onResendPhone() {
  if (resendCooldown.isCoolingDown) return

  clearErrors()
  isLoading.value = true

  const sendCode = currentStep.value === STEPS.PATIENT_VERIFY
    ? registerApi.sendPatientPhoneVerificationCode
    : registerApi.sendPhoneVerificationCode

  try {
    await sendCode()
    resendFeedback.value = 'A new verification code has been sent.'
    resendCooldown.startCooldown()
  } catch (error) {
    formError.value = extractRegisterError(error, 'code')
  } finally {
    isLoading.value = false
  }
}

async function onVerifyPhone(code) {
  clearErrors()
  isLoading.value = true

  const verifyCode = currentStep.value === STEPS.PATIENT_VERIFY
    ? registerApi.verifyPatientPhoneCode
    : registerApi.verifyPhoneCode

  try {
    const { data } = await verifyCode(code)
    emailPoll.stop()
    if (currentStep.value === STEPS.PATIENT_VERIFY) {
      handleRedirect(data)
      return
    }
    if (data.step === 3) {
      navigateTo(STEPS.ACCOUNT_MANAGER)
    }
  } catch (error) {
    formError.value = extractRegisterError(error, 'code')
  } finally {
    isLoading.value = false
  }
}

async function onManagerSubmit(form) {
  clearErrors()
  isLoading.value = true

  try {
    const { data } = await registerApi.submitManager(form)
    if (data.step === 4) {
      navigateTo(STEPS.APPROVAL_WAIT)
    }
  } catch (error) {
    fieldErrors.value = extractFieldErrors(error)
    formError.value = extractRegisterError(error, 'email')
  } finally {
    isLoading.value = false
  }
}

async function onManagerSkip() {
  clearErrors()
  isLoading.value = true

  try {
    const { data } = await registerApi.skipManager()
    if (data.step === 4) {
      navigateTo(STEPS.APPROVAL_WAIT)
    }
  } catch (error) {
    formError.value = extractRegisterError(error, 'skip')
  } finally {
    isLoading.value = false
  }
}

watch(currentStep, (step) => {
  if (
    (step === STEPS.PRACTICE_VERIFY || step === STEPS.PATIENT_VERIFY)
    && flowData.verificationMethod === VERIFY_METHOD.EMAIL
  ) {
    emailPoll.start()
  } else {
    emailPoll.stop()
  }
})

onMounted(async () => {
  if (!props.config.canResume) {
    isResuming.value = false
    return
  }

  if (props.config.flow !== FLOWS.PATIENT && props.config.flow !== FLOWS.PRACTICE) {
    isResuming.value = false
    return
  }

  try {
    if (props.config.flow === FLOWS.PATIENT) {
      await loadPatientStatus()
    } else {
      await loadPracticeStatus()
    }
  } catch (error) {
    if (error.response?.status !== 401) {
      formError.value = extractRegisterError(error, 'registration')
    }
  } finally {
    isResuming.value = false
  }
})
</script>

<style scoped>
.step-fade-enter-active,
.step-fade-leave-active {
  transition: opacity 0.2s ease;
}

.step-fade-enter-from,
.step-fade-leave-to {
  opacity: 0;
}

.auth-register__resume {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 12rem;
  text-align: center;
}
</style>
